<script>
  import { format } from 'svelte-i18n';

  import { getNotificationsContext } from '@client/components/notifications';
  import { Spinner } from '@client/components/spinner';

  import { BizCardsService } from '@client/services/bizcards';

  import { GeneralDeleteForm } from '../index.js';

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  const getBusinessCard = async (card) => {
    let email = 'adminuser';

    if (card.html) {
      return card.html;
    }

    let getSender = card?.senders?.[0];

    if (getSender) {
      const username = getSender.username || email;

      if (username !== '*') {
        email = username;
      }
    }

    const data = {
      username: email,
      scratchblockname: card.rulename,
      scratchtemplate: card.htmltemplate,
      scratchparams: card.params,
      params: card.params,
      prependLines: 0,
      testData: {
        testsender: email
      }
    };

    card.loading = true;

    try {
      const response = await BizCardsService.preview(data);
      const { rendered } = response;

      card.loading = false;
      card.html = rendered;

      return rendered;
    } catch (error) {
      card.loading = false;
      console.error('Preview error:', error);
      throw error;
    }
  };

  const getBizCards = async () => {
    const cards = await BizCardsService.getAll();
    return cards.map((card) => ({
      label: card.rulename,
      value: card.rulename,
      ...card
    }));
  };

  const onDeleteBizCard = async (card) => {
    const response = await BizCardsService.delete(card);

    if (response?.status >= 400) {
      notify($format('label.FAILURE.DELETE_BUSINESS_CARD', { values: { rulename: card.rulename } }), 'error');
      throw new Error($format('label.FAILURE.DELETE_BUSINESS_CARD'));
    }

    notify($format('label.SUCCESS.DELETE', { values: { type: card.rulename } }));

    step.data = {
      id: card.rulename,
      rollbackData: {
        card: { ...card }
      }
    };
  };

  const rollbackDelete = async () => {
    if (!step.data?.rollbackData) {
      throw new Error($format('label.NO_ROLLBACK_DATA'));
    }

    const { card } = step.data.rollbackData;

    // Prepare business card data for recreation
    const cardData = {
      rulename: card.rulename,
      htmltemplate: card.htmltemplate,
      manifesttemplate: card.manifesttemplate,
      params: card.params,
      status: card.status || false,
      _id: null,
      _newObject: true
    };

    // Restore the deleted business card
    const response = await BizCardsService.update(cardData);

    if (response?.status >= 400) {
      notify($format('label.FAILURE.ROLLBACK_BUSINESS_CARD_DELETE'), 'error');
      throw new Error($format('label.FAILURE.ROLLBACK_BUSINESS_CARD_DELETE'));
    }

    notify($format('label.SUCCESS.ROLLBACK_BUSINESS_CARD_DELETE'));

    return { success: true };
  };
</script>

<GeneralDeleteForm
  bind:nextDisabled
  bind:step
  {onExecute}
  {onRollback}
  getFunction={getBizCards}
  deleteFunction={onDeleteBizCard}
  rollbackFunction={rollbackDelete}
  showPreview={true}
>
  <div slot="preview" let:item>
    {#if item}
      {#await getBusinessCard(item)}
        <div class="bizcard-preview loading">
          <Spinner show inline />
        </div>
      {:then html}
        <div class="bizcard-preview">
          <iframe
            tabindex="-1"
            scrolling="no"
            sandbox="allow-same-origin"
            title={item.rulename}
            srcdoc="<html><body><div>{html || ''}</div></body></html>"
          />
        </div>
      {:catch error}
        <div class="bizcard-preview error">
          <p>{$format('label.FAILED_TO_LOAD_PREVIEW')}</p>
        </div>
      {/await}
    {/if}
  </div>
</GeneralDeleteForm>

<style lang="scss">
  .bizcard-preview {
    background-color: var(--color-white);
    border: 1px solid var(--border-color);
    border-radius: var(--border-radius-base);
    box-sizing: border-box;
    height: 404px;
    margin: 0 auto;
    overflow: hidden;
    position: relative;
    width: 190px;

    iframe {
      border: none;
      height: 808px;
      pointer-events: none;
      transform: scale(0.5);
      transform-origin: top left;
      width: 380px;
    }

    &.loading,
    &.error {
      align-items: center;
      display: flex;
      justify-content: center;
      padding: var(--padding-large);
    }

    &.error {
      color: var(--color-red-600);

      p {
        margin: 0;
      }
    }
  }
</style>
